// Pure helpers for "what is still deliverable" on a Modern Trade sale order.
// Owner ruling 2026-11-04: an SO may carry several delivery notes (the factory
// delivers in parts). The backend (MtDeliveryCreate) has the final word: it
// caps each DO by received lot minus earlier non-cancelled DOs (lot mode), or
// by SO line qty minus delivered qty (no-lot mode). These helpers only decide
// whether to offer the button and what to pre-fill. Vue-free for jest.

const DELIVERY_CANCELLED = 9;
const SO_CLOSED = 6;
const SO_CANCELLED = 9;

// Σ delivered qty per product over non-cancelled delivery notes.
export function deliveredByProduct(deliveries, deliveryItems) {
  const cancelled = {};
  (deliveries || []).forEach(d => {
    if (d && Number(d.status) === DELIVERY_CANCELLED) cancelled[d.id] = true;
  });
  const out = {};
  (deliveryItems || []).forEach(di => {
    if (!di || cancelled[di.delivery_id]) return;
    out[di.product_id] = (out[di.product_id] || 0) + (Number(di.qty) || 0);
  });
  return out;
}

// No-lot mode (outright type 1/3): one row per SO product with the qty still
// to deliver = Σ SO line qty − Σ delivered (never below 0). SO line order.
export function productRemainingRows(items, deliveries, deliveryItems) {
  const delivered = deliveredByProduct(deliveries, deliveryItems);
  const rows = [];
  const byProduct = {};
  (items || []).forEach(i => {
    if (!i) return;
    let r = byProduct[i.product_id];
    if (!r) {
      r = { product_id: i.product_id, product_code: i.product_code, product_name: i.product_name, ordered: 0 };
      byProduct[i.product_id] = r;
      rows.push(r);
    }
    r.ordered += Number(i.qty) || 0;
  });
  rows.forEach(r => {
    r.delivered = delivered[r.product_id] || 0;
    r.remaining = Math.max(0, r.ordered - r.delivered);
  });
  return rows;
}

// Lot mode (consignment / outright factory): lots with qty_available > 0
// (qty_available comes from the BE and already nets out earlier DOs).
export function deliverableLots(receivedLots) {
  return (receivedLots || []).filter(l => l && Number(l.qty_available) > 0);
}

// Whether "create delivery" is offered: the SO is confirmed or later, not
// closed / cancelled, and either it has no delivery note yet (first DO, the
// old rule) or something is still deliverable.
export function canCreateDelivery({ canMutate, status, needsLot, deliveries, deliveryItems, receivedLots, items }) {
  const st = Number(status);
  if (!canMutate || !(st >= 2) || st === SO_CLOSED || st === SO_CANCELLED) return false;
  if (!(deliveries || []).length) return true;
  if (needsLot) return deliverableLots(receivedLots).length > 0;
  return productRemainingRows(items, deliveries, deliveryItems).some(r => r.remaining > 0);
}
